import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "@/components/seo/SEO";
import { MUSEUM_LD } from "@/lib/jsonld";
import { CheckCircle2 } from "lucide-react";

const FEATURE_ROMANS = ["I", "II", "III", "IV", "V", "VI"];

export default function Museo() {
    const { t } = useTranslation();
    const features = t("museo.features", { returnObjects: true });

    return (
        <article className="pt-32 pb-24 paper-grain">
            <SEO
                title={t("museo.seo.title")}
                description={t("museo.seo.description")}
                keywords="museo Ocaña, tenería siglo XV, judería Ocaña, guantes de perrillo, visitas guiadas Ocaña, patrimonio Toledo, qué visitar en Ocaña, turismo Ocaña"
                path="/museo"
                breadcrumbs={[
                    { name: t("nav.home"), item: "/" },
                    { name: t("nav.museo"), item: "/museo" },
                ]}
                jsonLd={MUSEUM_LD}
            />

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-6 text-center mb-32">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    className="roman-numeral"
                >
                    — {t("museo.anno")} —
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="font-display font-light text-[clamp(2.2rem,6vw,5.5rem)] leading-[0.95] text-ivory mt-8"
                >
                    {t("museo.title1")}
                    <span className="block italic text-gold">{t("museo.title2")}</span>
                </motion.h1>
                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.8, duration: 1 }}
                    className="line-gold mx-auto my-10 origin-left"
                />
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 1.2 }}
                    className="font-body text-lg text-stone max-w-2xl mx-auto leading-relaxed"
                >
                    {t("museo.subtitle")}
                </motion.p>
            </section>

            {/* Historia */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-center mb-32">
                <div className="md:col-span-5">
                    <div className="aspect-[4/5] overflow-hidden">
                        <img
                            src="/images/platos/artesonado.webp"
                            alt="Artesonado original del siglo XV en la antigua tenería de Ocaña"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.2 }}
                    className="md:col-span-6 md:col-start-7"
                >
                    <p className="roman-numeral">— {t("museo.history.kicker")} —</p>
                    <h2 className="font-display text-5xl md:text-7xl text-ivory font-light leading-tight mt-6">
                        {t("museo.history.title1")}
                        <span className="block italic text-gold">{t("museo.history.title2")}</span>
                    </h2>
                    <div className="line-gold my-10" />
                    <p className="font-body text-stone leading-[1.8] drop-cap">
                        {t("museo.history.p1")}{" "}
                        <em className="text-gold not-italic">{t("museo.history.p1Highlight")}</em>
                        {t("museo.history.p1b")}
                    </p>
                    <p className="font-body text-stone/80 leading-relaxed mt-6">
                        {t("museo.history.p2a")}{" "}
                        <em className="text-ivory not-italic">{t("museo.history.p2firm")}</em>
                        {t("museo.history.p2b")}
                    </p>
                </motion.div>
            </section>

            {/* Museo segunda planta */}
            <section className="parchment py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-center">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1.2 }}
                        className="md:col-span-6 order-2 md:order-1"
                    >
                        <p className="font-editorial italic text-sm tracking-[0.3em] uppercase text-oxblood">
                            — {t("museo.secondFloor.kicker")} —
                        </p>
                        <h2 className="font-display text-5xl md:text-7xl text-ink font-light leading-tight mt-6">
                            {t("museo.secondFloor.title1")}
                            <span className="block italic text-oxblood">
                                {t("museo.secondFloor.title2")}
                            </span>
                        </h2>
                        <div className="w-[60px] h-px bg-oxblood my-10" />
                        <p className="font-body text-ink-soft leading-[1.8] drop-cap">
                            {t("museo.secondFloor.p1")}
                        </p>
                        <p className="font-body text-ink-soft/80 leading-relaxed mt-6">
                            {t("museo.secondFloor.p2")}
                        </p>
                    </motion.div>
                    <div className="md:col-span-5 md:col-start-8 order-1 md:order-2">
                        <div className="aspect-[4/5] overflow-hidden">
                            <img
                                src="/images/platos/tinaja.webp"
                                alt="Tinaja cerámica original conservada en el museo de la antigua tenería de Ocaña"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Visitas guiadas */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-32">
                <motion.header
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="text-center mb-20"
                >
                    <p className="roman-numeral">— {t("museo.tours.kicker")} —</p>
                    <h2 className="font-display text-4xl md:text-6xl text-ivory font-light mt-6">
                        {t("museo.tours.title1")}
                        <span className="block italic text-gold">{t("museo.tours.title2")}</span>
                    </h2>
                </motion.header>
                <div className="grid md:grid-cols-3 gap-px bg-gold/15">
                    {features.map((c, i) => (
                        <motion.div
                            key={c.t}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-30px" }}
                            transition={{ duration: 0.9, delay: i * 0.08 }}
                            className="bg-bg p-10 hover:bg-bg-warm transition-colors duration-700 group"
                        >
                            <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold/70">
                                N.º {FEATURE_ROMANS[i]}
                            </p>
                            <CheckCircle2 size={18} className="text-gold mt-6 mb-4" />
                            <h3 className="font-display text-2xl text-ivory group-hover:text-gold transition-colors duration-500">
                                {c.t}
                            </h3>
                            <p className="font-body text-stone text-sm leading-relaxed mt-3">
                                {c.d}
                            </p>
                        </motion.div>
                    ))}
                </div>
                <div className="text-center mt-20">
                    <a href="tel:+34925484633" className="btn-solid-alt" data-cursor="hover">
                        {t("museo.tours.consult")}
                    </a>
                </div>
            </section>

            {/* Galería del museo */}
            <section className="max-w-6xl mx-auto px-6 py-16">
                <div className="text-center mb-16">
                    <p className="roman-numeral">— Galería —</p>
                    <h2 className="font-display text-4xl md:text-5xl text-ivory font-light mt-4">
                        El <span className="italic text-gold">edificio</span>
                    </h2>
                    <div className="line-gold mx-auto mt-8" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                        { nombre: "fachada-nocturna", alt: "Fachada nocturna del Restaurante Museo Tenería en Ocaña" },
                        { nombre: "entrada-museo", alt: "Entrada al Museo Tenería con bargueño español del siglo XVII" },
                        { nombre: "arcón-epoca", alt: "Arcón original de época en el museo de la tenería de Ocaña" },
                        { nombre: "mecedora-cuero", alt: "Mecedora de cuero antigua conservada en el museo" },
                        { nombre: "cueva", alt: "Cueva original del siglo XV en el Restaurante Museo Tenería" },
                        { nombre: "calle-ocana", alt: "Calle típica de la antigua judería de Ocaña junto a la Tenería" },
                    ].map((foto, i) => (
                        <motion.div
                            key={foto.nombre}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7, delay: i * 0.08 }}
                            className="aspect-square overflow-hidden group"
                        >
                            <img
                                src={`/images/platos/${foto.nombre}.webp`}
                                alt={foto.alt}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                loading="lazy"
                            />
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Certificación */}
            <section className="max-w-3xl mx-auto px-6 pb-16">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5 }}
                    className="relative border border-gold/30 p-12 text-center"
                >
                    <div className="absolute -inset-px border border-gold/15" style={{ borderStyle: "double" }} />
                    <p className="roman-numeral relative">— {t("museo.award.kicker")} —</p>
                    <p className="font-display italic text-2xl md:text-3xl text-ivory leading-snug mt-6 relative">
                        {t("museo.award.quote")}
                    </p>
                </motion.div>
            </section>
        </article>
    );
}